import {
  useCallback,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import {
  MEMBER_ACTIVITY_MS,
  type MemberActivityMap,
} from "@/types/frontend-types";
import type {
  LiveSignalTimer,
  PushMemberActivityInput,
} from "./session-live-signals.types";

const MEMBER_ACTIVITY_EXCERPT_WORD_COUNT = 10;

interface UseSessionMemberActivitySignalsInput {
  initialLoadCompletedAtRef: MutableRefObject<number | null>;
}

interface SessionMemberActivitySignals {
  memberActivity: MemberActivityMap;
  pushMemberActivity: (input: PushMemberActivityInput) => void;
  resetMemberActivity: () => void;
  cleanupMemberActivityTimers: () => void;
}

export function useSessionMemberActivitySignals({
  initialLoadCompletedAtRef,
}: UseSessionMemberActivitySignalsInput): SessionMemberActivitySignals {
  const [memberActivity, setMemberActivity] = useState<MemberActivityMap>({});
  const memberActivityTimeoutsRef = useRef<Record<string, LiveSignalTimer>>({});

  const pushMemberActivity = useCallback(
    (input: PushMemberActivityInput): void => {
      if (initialLoadCompletedAtRef.current == null) return;

      const { memberId, kind, excerpt } = input;
      const pushedAt = Date.now();
      setMemberActivity((prev) => ({
        ...prev,
        [memberId]: { kind, excerpt, pushedAt },
      }));

      const existingTimeout = memberActivityTimeoutsRef.current[memberId];
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      memberActivityTimeoutsRef.current[memberId] = setTimeout(() => {
        setMemberActivity((prev) => {
          const next = { ...prev };
          delete next[memberId];
          return next;
        });
        delete memberActivityTimeoutsRef.current[memberId];
      }, MEMBER_ACTIVITY_MS);
    },
    [initialLoadCompletedAtRef],
  );

  const resetMemberActivity = useCallback((): void => {
    setMemberActivity({});
  }, []);

  const cleanupMemberActivityTimers = useCallback((): void => {
    Object.values(memberActivityTimeoutsRef.current).forEach((timeout) => {
      clearTimeout(timeout);
    });
    memberActivityTimeoutsRef.current = {};
  }, []);

  return {
    memberActivity,
    pushMemberActivity,
    resetMemberActivity,
    cleanupMemberActivityTimers,
  };
}

export function makeMemberActivityExcerpt({
  text,
  wordCount = MEMBER_ACTIVITY_EXCERPT_WORD_COUNT,
}: {
  text: string;
  wordCount?: number;
}): string {
  const trimmed = text.trim();
  if (!trimmed) return "";

  const allWords = trimmed.split(/\s+/);
  const head = allWords.slice(0, wordCount).join(" ");
  return allWords.length > wordCount ? `${head}\u2026` : head;
}
